/**
 * ContentReferenceAutocomplete - Autocomplete component for selecting content references
 * @module content-detail/content-references/ContentReferenceAutocomplete
 */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLazyQuery } from '@apollo/client'
import { Autocomplete, TextField, Chip, CircularProgress } from '@mui/material'
import { SEARCH_CONTENT } from './queries'
import type { ContentReference } from '../types'

interface ContentReferenceAutocompleteProps {
  /** Selected content references */
  value: ContentReference[]
  /** Called when selection changes */
  onChange: (value: ContentReference[]) => void
  /** Content type to search (e.g., "tag") */
  contentType: string
  /** Content bundle to filter (e.g., "admin_category") */
  bundle?: string
  /** Filter type (optional, alternative to bundle) */
  filterType?: string
  /** Label for the field */
  label: string
  /** Whether field is disabled */
  disabled: boolean
  /** Whether to allow multiple selections */
  multiple: boolean
}

/**
 * ContentReferenceAutocomplete Component
 *
 * An autocomplete dropdown for searching and selecting content references.
 * Features:
 * - Prefetch initial options on dropdown open
 * - Search with 300ms debounce
 * - Multi-select support
 * - Shows published status badges
 * - Limits results to 50 with message
 */
function ContentReferenceAutocomplete (props: ContentReferenceAutocompleteProps) {
  const {
    value = [],
    onChange,
    contentType,
    bundle,
    filterType,
    label = 'Content References',
    disabled = false,
    multiple = true,
  } = props

  // Local state
  const [inputValue, setInputValue] = useState<string>('')
  const [options, setOptions] = useState<ContentReference[]>(value) // Initialize with selected values
  const [hasSearched, setHasSearched] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // GraphQL lazy query for searching content
  const [searchContent, { loading, data, error }] = useLazyQuery(SEARCH_CONTENT, {
    fetchPolicy: 'network-only', // Always fetch fresh results
  })

  // Handle GraphQL errors
  useEffect(() => {
    if (error) {
      // eslint-disable-next-line no-console
      console.error('ContentReferenceAutocomplete GraphQL Error:', {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
        fullError: error,
      })
    }
  }, [error])

  // Cleanup debounce timer on unmount
  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    },
    [],
  )

  // Update options when data changes
  // Always include selected items in options to avoid MUI warnings
  useEffect(() => {
    if (data?.contentList?.content) {
      // Start with search results
      const searchResults = data.contentList.content

      // Merge with selected values - ensure selected items are always in options
      // Create a map by contentId to avoid duplicates
      const optionsMap = new Map<number, ContentReference>()

      // Add selected values first
      value.forEach((item) => {
        optionsMap.set(item.contentId, item)
      })

      // Add search results (won't overwrite selected items due to Map)
      searchResults.forEach(
        (
          item: {
            id: string
            contentId: number
            contentType: string
            title?: string | null
            published?: boolean | null
            titleGroupId?: string | null
          } | null,
        ) => {
          if (item && !optionsMap.has(item.contentId)) {
            // Transform search result to ContentReference format
            optionsMap.set(item.contentId, {
              id: item.id,
              contentId: item.contentId,
              contentType: item.contentType,
              title: item.title || '',
              published: item.published || false,
              titleGroupId: item.titleGroupId || '',
              referenceId: '', // Will be set when saved
              contentReferenceType: '', // Will be set when saved
            })
          }
        },
      )

      const newOptions = Array.from(optionsMap.values())
      setOptions(newOptions)
    }
  }, [data, value, multiple, label])

  // Sync value into options whenever value changes (ensures selected items are always in options)
  useEffect(() => {
    if (value.length > 0) {
      setOptions((prevOptions) => {
        const optionsMap = new Map<number, ContentReference>()

        // Add existing options
        prevOptions.forEach((opt) => optionsMap.set(opt.contentId, opt))

        // Add value items (won't overwrite if already present)
        value.forEach((item) => {
          if (!optionsMap.has(item.contentId)) {
            optionsMap.set(item.contentId, item)
          }
        })

        return Array.from(optionsMap.values())
      })
    }
  }, [value])

  // Cleanup debounce timer on unmount
  const handleOpen = useCallback(() => {
    setIsOpen(true)
    if (!hasSearched) {
      const variables: {
        contentType: string
        title: string
        limit: number
        language: string[]
        bundle?: string
        filterType?: string
      } = {
        contentType,
        title: '%', // Match all
        limit: 50,
        language: ['en', '_primary'], // Hardcoded to English for MVP
      }

      if (bundle) {
        variables.bundle = bundle
      }

      if (filterType) {
        variables.filterType = filterType
      }

      searchContent({ variables })
      setHasSearched(true)
    }
  }, [hasSearched, searchContent, contentType, bundle, filterType])

  // Handle dropdown close
  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  // Handle input change with debounce
  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, newInputValue: string, reason: string) => {
      // Handle 'reset' reason differently for single vs multi-select
      // 'reset' means "display the selected value" in single-select mode
      // but "clear the input" in multi-select mode
      if (reason === 'reset') {
        if (multiple) {
          setInputValue('') // Clear for multi-select (after selecting a chip)
        } else {
          setInputValue(newInputValue) // Keep for single-select (display the selected value)
        }
        return
      }

      setInputValue(newInputValue)

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Debounce search for 300ms
      debounceTimerRef.current = setTimeout(() => {
        const searchTerm = newInputValue.trim()

        const variables: {
          contentType: string
          title: string
          limit: number
          language: string[]
          bundle?: string
          filterType?: string
        } = {
          contentType,
          title: searchTerm ? `%${searchTerm}%` : '%', // Partial match
          limit: 50,
          language: ['en', '_primary'], // Hardcoded to English for MVP
        }

        if (bundle) {
          variables.bundle = bundle
        }

        if (filterType) {
          variables.filterType = filterType
        }

        // Search with partial match
        searchContent({ variables })
      }, 300)
    },
    [searchContent, contentType, bundle, filterType, multiple],
  )

  // Handle selection change
  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: ContentReference | ContentReference[] | null) => {
      // Handle both single and multiple selection
      if (Array.isArray(newValue)) {
        onChange(newValue)
      } else if (newValue) {
        onChange([newValue])
      } else {
        onChange([])
      }
    },
    [onChange],
  )

  // Get option label
  const getOptionLabel = useCallback((option: ContentReference) => option.title || '', [])

  // Check if two options are equal
  const isOptionEqualToValue = useCallback(
    (option: ContentReference, val: ContentReference) => option.contentId === val.contentId,
    [],
  )

  // Render option with published badge
  const renderOption = useCallback(
    (optionProps: React.HTMLAttributes<HTMLLIElement>, option: ContentReference) => (
      <li {...optionProps} key={option.contentId}>
        {option.title}
        {option.published && (
          <Chip label="Published" size="small" color="success" sx={{ ml: 1, height: 20 }} />
        )}
      </li>
    ),
    [],
  )

  // Render selected tags
  const renderTags = useCallback(
    (tagValue: ContentReference[], getTagProps: (args: { index: number }) => object) =>
      tagValue.map((option, index) => (
        <Chip
          label={option.title}
          {...getTagProps({ index })}
          disabled={disabled}
          key={option.contentId}
        />
      )),
    [disabled],
  )

  // Filter options to hide already-selected items from dropdown
  // BUT keep them in options array for MUI validation
  const filterOptions = useCallback(
    (opts: ContentReference[]) => {
      // Get set of selected contentIds for quick lookup
      const selectedContentIds = new Set(value.map((item) => item.contentId))

      // Filter out already selected items from dropdown display
      return opts.filter((option) => !selectedContentIds.has(option.contentId))
    },
    [value],
  )

  // Show message if results are limited AND dropdown is open
  const helperText =
    isOpen && data?.contentList?.content?.length === 50
      ? 'Showing first 50 results. Type more to refine your search.'
      : ''

  // For single-select, transform value from array to single object
  const autocompleteValue = multiple ? value : value[0] || null

  return (
    <Autocomplete<ContentReference, boolean, undefined, undefined>
      multiple={multiple}
      value={autocompleteValue}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onOpen={handleOpen}
      onClose={handleClose}
      options={options}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      renderTags={renderTags}
      loading={loading}
      disabled={disabled}
      filterOptions={filterOptions} // Hide selected items from dropdown
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          sx={(theme) => ({
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ccc',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#999',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
          })}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

export default ContentReferenceAutocomplete
